<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType, EquipType } from '@/types/equip'
import { computed, ref } from 'vue'
import EquipInfo from '../../item/components/EquipInfo.vue'
import { socketEmitAsync } from '@/utils/web-socket'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import { getEquipName } from '@/utils/equip'
import { QuestionFilled } from '@element-plus/icons-vue'

const userStore = useUserStore()

const CONVERT_PRICE = 100000
const equipInfo = ref<EquipItemType>()

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      if (item.quality !== 'unique') return false
      const equippedIdList = Object.values(userStore.equippedList)
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

const notConfirm = ref(false)
const convertResShow = ref(false)
function handleConvert() {
  if (!equipInfo.value) return
  if (userStore.gold < CONVERT_PRICE) return ElMessage.warning('您的金币不足')
  for (const routine of userStore.equippedRoutineList) {
    let key: EquipType
    for (key in routine.equippedList) {
      if (routine.equippedList[key] === equipInfo.value.id) {
        return ElMessage.warning('无法转换穿戴中的装备，若未装备，请检查装备套路')
      }
    }
  }
  const { id } = equipInfo.value
  if (notConfirm.value) return convert()
  ElMessageBox.confirm(`确定将<span class="unique">${equipInfo.value.name}</span>转换为套装内的其它部件吗？`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(convert)
    .catch(() => {})

  async function convert() {
    const equipId = await socketEmitAsync<number>({ type: 'convert', data: { id } })
    if (!equipInfo.value) return
    const item = userStore.itemList.find((item) => item.id === id)
    if (!item) return
    item.equipId = equipId
    const curEquipInfo = itemList.value.find((item) => item.id === id)
    equipInfo.value = curEquipInfo
    userStore.gold -= CONVERT_PRICE
  }
}
</script>

<template>
  <div class="convert">
    <div class="convert-table border-wrap">
      <h4>转换台</h4>
      <div class="convert-equip">
        <CommonBtn style="width: 100%" @done="equipInfo = undefined">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>转换说明</h4>
            <p>
              1. 转换功能为消耗<span class="gold">金币</span>将您放入的<span class="unique">独特</span>装备，转为其<span class="info">套装内</span
              >的其他装备部件。
            </p>
            <p>2. 不会转换为当前放入转换的装备部件。</p>
            <p>3. 转换时，会保留<span class="info">强化等级</span>及<span class="info">升星等级</span>。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <p>转换花费：{{ formatNumberWithCommas(CONVERT_PRICE) }}</p>
          <p style="align-items: center; justify-content: flex-start">免确认：<el-switch v-model="notConfirm" size="small" /></p>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleConvert">开始转换</el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap">
      <h4>背包</h4>
      <p class="cur-gold">💰 {{ formatNumberWithCommas(userStore.gold) }}</p>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of itemList" :key="item.id">
            <CommonBtn size="small" @done="equipInfo = item" :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }">
              <span v-html="getEquipName(item, item.type)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>

    <el-dialog v-model="convertResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">转换成功</span>
      </h2>
      <template v-if="equipInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <CommonBtn size="small">
              <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
            </CommonBtn>
          </template>
          <EquipInfo :equip-info="equipInfo" :show-action="false" />
        </el-popover>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="convertResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.convert {
  display: flex;
  flex-flow: column nowrap;
  .convert-table {
    padding: 10px 8px;
    margin-bottom: 10px;
    position: relative;
    .convert-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
}
</style>
